import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useAuth } from '../modules/auth'
import { filterOtherContact } from '../services/contacts'

// Kept in sync with ChecklistAssignee in TextEditor.tsx.
// Duplicated to keep this component decoupled from the editor's internal types.
export type ChecklistAssigneeSource = 'board' | 'contact' | 'freetext'
export interface ChecklistAssignee {
  id: string
  name: string
  source: ChecklistAssigneeSource
  email?: string
  hasLicense?: boolean
  // Set on placeholder entries seeded from a task assignee whose name
  // didn't match the board roster — preserves the original
  // board_person_id so it can be sent back on save without lookup.
  _bpid?: string
}

// A board member pulled from the current board, as passed from the parent form.
// Mirrors what useBoardPersons.ts produces via optionsForSendAlert +
// boardPersonsForSendAlert — we accept the union here so the caller can pass
// either shape without converting.
//
// `id` is the user id (used for alert routing and dedup against task
// assignees). `boardPersonId` is the board-person join-table id; we carry it
// through so create-task-from-checklist can build card_persons rows without
// re-fetching the board roster and re-mapping userId → bpid.
export interface ChecklistBoardMember {
  id: string
  name: string
  email?: string
  isDeleted?: boolean
  boardPersonId?: string
}

// A saved contact fetched via filterOtherContact during typeahead.
// The real contact service returns a richer object; we pick only the fields we
// display + need to decide on alerting.
interface ContactMatch {
  id: string
  name: string
  email?: string
  hasLicense?: boolean
}

interface ChecklistAssigneeDropdownProps {
  // Anchor rect of the icon that opened the dropdown (viewport coords).
  anchorRect: { top: number; bottom: number; left: number; right: number }
  // The assignees the item currently has — used to seed the UI.
  initialAssignees: ChecklistAssignee[]
  // All members of the current board. Filtered by `isDeleted` for display.
  boardMembers: ChecklistBoardMember[]
  // Fired when the user closes the dropdown (outside click, Escape, Done).
  // Called with the new assignee list — the editor diffs and sends alerts.
  onClose: (assignees: ChecklistAssignee[]) => void
  // Open the Create New Contact modal pre-filled with a name. Caller wires this
  // to the existing AddNewContactModal flow so we don't duplicate that UX.
  onOpenCreateContact?: (name: string) => void
  // Open the existing contact's details modal.
  onOpenContactDetails?: (contactId: string) => void
  theme?: string
}

const ChecklistAssigneeDropdown: React.FC<ChecklistAssigneeDropdownProps> = ({
  anchorRect,
  initialAssignees,
  boardMembers,
  onClose,
  onOpenCreateContact,
  onOpenContactDetails,
  theme,
}) => {
  const { currentUser } = useAuth()
  const userId: string | undefined = currentUser?.data?.user?.id
  const companyId: string | undefined =
    currentUser?.data?.user?.default_organization_id ||
    currentUser?.data?.user?.organizationId ||
    undefined

  const [assignees, setAssignees] = useState<ChecklistAssignee[]>(initialAssignees)
  const [query, setQuery] = useState('')
  const [contactMatches, setContactMatches] = useState<ContactMatch[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const closedRef = useRef(false)

  // Always call onClose with the latest assignees, exactly once.
  const close = useCallback(() => {
    if (closedRef.current) return
    closedRef.current = true
    onClose(assignees)
  }, [assignees, onClose])

  // Outside click → close
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current && !containerRef.current.contains(target)) {
        close()
      }
    }
    // Delay one tick so the click that opened us doesn't also close us
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
    }
  }, [close])

  // Escape → close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [close])

  // Autofocus the search input
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Debounced contact search. Only runs when the user has typed at least 2 chars
  // AND the query doesn't match an existing board member name — this keeps
  // the API quiet while the user is picking from the board list.
  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setContactMatches([])
      return
    }
    const matchesBoard = boardMembers.some(
      (m) => m.name.toLowerCase().includes(trimmed.toLowerCase())
    )
    // Still query contacts even on a board match — the user might be adding
    // both a board member and a contact that share a first name.
    if (!userId) {
      setContactMatches([])
      return
    }
    let cancelled = false
    setContactsLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res: any = await filterOtherContact(trimmed, userId, companyId ?? '')
        if (cancelled) return
        // Backend payload is encrypted in some places and not others — accept
        // both shapes and fall back to an empty list on anything weird.
        const list: any[] =
          res?.data?.data?.contacts ||
          res?.data?.data ||
          res?.data?.contacts ||
          []
        const mapped: ContactMatch[] = Array.isArray(list)
          ? list
              .filter((c) => c && (c.fullName || c.name))
              .map((c) => ({
                id: c.id ?? c._id ?? c.contactId ?? String(Math.random()),
                name: c.fullName ?? c.name ?? '',
                email: c.email ?? undefined,
                // Backend doesn't yet tell us about license; default false.
                hasLicense: false,
              }))
          : []
        setContactMatches(mapped)
      } catch {
        if (!cancelled) setContactMatches([])
      } finally {
        if (!cancelled) setContactsLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // matchesBoard read only to avoid unused warnings
    void matchesBoard
  }, [query, userId, companyId, boardMembers])

  const toggleBoardMember = (m: ChecklistBoardMember) => {
    setAssignees((prev) => {
      const exists = prev.find((a) => a.source === 'board' && a.id === m.id)
      if (exists) return prev.filter((a) => !(a.source === 'board' && a.id === m.id))
      return [
        ...prev,
        {
          id: m.id,
          name: m.name,
          source: 'board',
          email: m.email,
          // Stamp the board_person_id at pick time so downstream flows
          // (create-task-from-checklist) can build card_persons records
          // without re-resolving userId → bpid against the board roster.
          ...(m.boardPersonId ? { _bpid: m.boardPersonId } : {}),
        },
      ]
    })
  }

  const toggleContact = (c: ContactMatch) => {
    setAssignees((prev) => {
      const exists = prev.find((a) => a.source === 'contact' && a.id === c.id)
      if (exists) return prev.filter((a) => !(a.source === 'contact' && a.id === c.id))
      return [
        ...prev,
        { id: c.id, name: c.name, source: 'contact', email: c.email, hasLicense: c.hasLicense },
      ]
    })
  }

  const addFreeText = () => {
    const name = query.trim()
    if (!name) return
    // Already assigned? No-op.
    if (assignees.some((a) => a.name.toLowerCase() === name.toLowerCase())) {
      setQuery('')
      return
    }
    setAssignees((prev) => [
      ...prev,
      { id: `freetext_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name, source: 'freetext' },
    ])
    setQuery('')
  }

  const removeAssignee = (a: ChecklistAssignee) => {
    setAssignees((prev) =>
      prev.filter((x) => !(x.source === a.source && x.id === a.id))
    )
  }

  // Filter board members by the search query. Deleted members are hidden.
  // Members already assigned (selected pills above) are also hidden from the
  // pick list — the user's request: don't surface already-assigned people as
  // candidates to add. They remain visible as pills with a remove button.
  const assignedBoardIds = useMemo(
    () => new Set(assignees.filter((a) => a.source === 'board').map((a) => a.id)),
    [assignees]
  )
  const assignedNamesLower = useMemo(
    () => new Set(assignees.map((a) => a.name.trim().toLowerCase())),
    [assignees]
  )
  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase()
    return boardMembers
      .filter((m) => !m.isDeleted)
      .filter((m) => !assignedBoardIds.has(m.id))
      // Catch placeholder board entries that were merged in by name only
      // (no real id match against the roster) so the same person doesn't
      // appear as both a pill and a pickable row.
      .filter((m) => !assignedNamesLower.has(m.name.trim().toLowerCase()))
      .filter((m) => (q ? m.name.toLowerCase().includes(q) : true))
  }, [query, boardMembers, assignedBoardIds, assignedNamesLower])

  const filteredContactMatches = useMemo(() => {
    const assignedContactIds = new Set(
      assignees.filter((a) => a.source === 'contact').map((a) => a.id)
    )
    return contactMatches
      .filter((c) => !assignedContactIds.has(c.id))
      .filter((c) => !assignedNamesLower.has(c.name.trim().toLowerCase()))
  }, [contactMatches, assignees, assignedNamesLower])

  // Position the dropdown below the anchor button, clamped to the viewport.
  const style: React.CSSProperties = useMemo(() => {
    const W = 300
    const top = anchorRect.bottom + 4
    let left = anchorRect.left
    if (typeof window !== 'undefined' && left + W > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - W - 8)
    }
    return {
      position: 'fixed',
      top,
      left,
      width: W,
      zIndex: 10001,
    }
  }, [anchorRect])

  const canAddFreeText =
    query.trim().length > 0 &&
    !assignedNamesLower.has(query.trim().toLowerCase()) &&
    !filteredMembers.some((m) => m.name.toLowerCase() === query.trim().toLowerCase()) &&
    !filteredContactMatches.some((c) => c.name.toLowerCase() === query.trim().toLowerCase())

  return (
    <div
      ref={containerRef}
      className={`ql-checklist-assignee-dropdown ${theme === 'dark' ? 'dark' : ''}`}
      style={style}
      onMouseDown={(e) => {
        // Keep focus inside the dropdown — we handle outside clicks via the listener.
        e.stopPropagation()
      }}
    >
      <div className="ql-checklist-assignee-header">
        <span>Assign to</span>
        <button
          type="button"
          className="ql-checklist-assignee-done"
          onClick={close}
          title="Done"
        >
          Done
        </button>
      </div>

      <div className="ql-checklist-assignee-search">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search or add a name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              // Only pick someone who is actually in the list — a board member
              // or a matched contact. A typed name that matches nobody does
              // NOT become a pill on Enter; use "Add <name> to contacts" for
              // that, so a half-typed name can't be assigned by accident.
              if (filteredMembers.length === 1) {
                toggleBoardMember(filteredMembers[0])
                setQuery('')
              } else if (filteredContactMatches.length === 1) {
                toggleContact(filteredContactMatches[0])
                setQuery('')
              }
            }
          }}
        />
        {canAddFreeText && (
          <button
            type="button"
            className="ql-checklist-assignee-add-freetext"
            onClick={addFreeText}
            title="Add this name"
            aria-label="Add this name"
          >
            &#10003;
          </button>
        )}
      </div>

      {assignees.length > 0 && (
        <div className="ql-checklist-assignee-pills">
          {assignees.map((a) => (
            <span key={`${a.source}:${a.id}`} className={`ql-checklist-assignee-pill source-${a.source}`}>
              {a.name}
              <button
                type="button"
                className="ql-checklist-assignee-pill-remove"
                onClick={() => removeAssignee(a)}
                title={`Remove ${a.name}`}
                aria-label={`Remove ${a.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="ql-checklist-assignee-list">
        {filteredMembers.length === 0 && filteredContactMatches.length === 0 && query.trim() === '' && (
          <div className="ql-checklist-assignee-empty">No board members yet</div>
        )}

        {filteredMembers.length > 0 && (
          <>
            <div className="ql-checklist-assignee-section-label">Board members</div>
            {filteredMembers.map((m) => (
              <button
                key={`board_${m.id}`}
                disabled={m.isDeleted}
                type="button"
                className="ql-checklist-assignee-row"
                onClick={() => toggleBoardMember(m)}
              >
                <span className="ql-checklist-assignee-checkmark">+</span>
                <span className="ql-checklist-assignee-name">{m.name}</span>
              </button>
            ))}
          </>
        )}

        {query.trim().length >= 2 && (filteredContactMatches.length > 0 || contactsLoading) && (
          <>
            <div className="ql-checklist-assignee-section-label">Contacts</div>
            {contactsLoading && <div className="ql-checklist-assignee-empty">Searching…</div>}
            {filteredContactMatches.map((c) => (
              <div
                key={`contact_${c.id}`}
                className="ql-checklist-assignee-row contact"
              >
                <button
                  type="button"
                  className="ql-checklist-assignee-row-main"
                  onClick={() => toggleContact(c)}
                >
                  <span className="ql-checklist-assignee-checkmark">+</span>
                  <span className="ql-checklist-assignee-name">{c.name}</span>
                  {c.email && <span className="ql-checklist-assignee-email">{c.email}</span>}
                </button>
                {onOpenContactDetails && (
                  <button
                    type="button"
                    className="ql-checklist-assignee-row-icon"
                    title="View contact"
                    aria-label="View contact"
                    onClick={() => onOpenContactDetails(c.id)}
                  >
                    &#9432;
                  </button>
                )}
              </div>
            ))}
          </>
        )}

        {canAddFreeText && onOpenCreateContact && (
          <button
            type="button"
            className="ql-checklist-assignee-row add-contact"
            onClick={() => {
              const name = query.trim()
              addFreeText()
              onOpenCreateContact(name)
            }}
          >
            <span className="ql-checklist-assignee-checkmark">+</span>
            <span className="ql-checklist-assignee-name">
              Add <strong>{query.trim()}</strong> to contacts
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

export default ChecklistAssigneeDropdown
